import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import '../../styles/VerificationModal.css';

interface VerificationModalProps {
  onSendVerification: () => void;
  onClose: () => void;
  sending: boolean;
  userEmail?: string;
}

const VerificationModal: React.FC<VerificationModalProps> = ({ 
  onSendVerification, 
  onClose, 
  sending,
  userEmail
}) => {
  const [countdown, setCountdown] = useState<number>(0);

  // Check localStorage for existing countdown
  useEffect(() => {
    const emailSentTimestamp = localStorage.getItem('verificationEmailSent');
    if (emailSentTimestamp) {
      const elapsed = Math.floor((Date.now() - parseInt(emailSentTimestamp)) / 1000);
      const cooldown = 60; // 60 seconds cooldown
      
      if (elapsed < cooldown) {
        setCountdown(cooldown - elapsed);
      }
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (countdown <= 0) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [countdown]);

  const handleSendVerification = () => {
    if (countdown > 0) return;
    
    // Set timestamp in localStorage
    localStorage.setItem('verificationEmailSent', Date.now().toString());
    setCountdown(60); // Reset countdown to 60 seconds
    
    // Call the onSendVerification prop
    onSendVerification();
  };

  return (
    <div className="verification-modal-overlay">
      <div className="verification-modal">
        <button 
          className="verification-modal-close" 
          onClick={onClose} 
          aria-label="Close verification modal"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <div className="verification-modal-header">
          <FontAwesomeIcon icon={faEnvelope} className="verification-modal-icon" />
          <h3>Email Verification Required</h3>
        </div>
        
        <div className="verification-modal-content">
          <p>You need to verify your email address before you can apply for events.</p>
          
          {userEmail && (
            <p className="verification-email">
              Your email: <strong>{userEmail}</strong>
            </p>
          )}
          
          <p>We'll send you a verification link that you need to click to confirm your email.</p>
          
          <div className="verification-modal-actions">
            <button 
              className="verification-action-button secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              className="verification-action-button primary"
              onClick={handleSendVerification}
              disabled={sending || countdown > 0}
            >
              {sending ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="icon-left" /> Sending...
                </>
              ) : countdown > 0 ? (
                `Resend in ${countdown}s`
              ) : (
                'Send Verification Email'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;
